import { useEffect, useMemo, useState } from 'react';
import type { Environnement, TakiSdk } from '@taki/sdk';
import type { InstallationPort } from '@taki/ui';

/**
 * LES APPS INSTALLEES ET L'ENVIRONNEMENT DE L'ORG (mode et fuseau), lus dans SA base et mis en
 * forme pour le socle (`InstallationPort`, @taki/ui). Ils vivaient dans
 * `taki.config.json` jusqu'au 10/10/2026 : `docs/socle/apps-installees-en-base.md`.
 *
 * DEUX LECTURES, DEUX MOMENTS :
 *   - l'ENVIRONNEMENT se lit AVANT la connexion (`useEnvironnementDeLHote`,
 *     monte par `main.tsx`) : la bande STAGING s'affiche sur l'ecran de
 *     connexion. Le document est public, comme le branding. Un cache local par
 *     org donne la bande des le premier rendu d'une visite suivante ; la base le
 *     remplace des sa premiere lecture.
 *   - les APPS se lisent une fois connecte (membre actif) : `App.tsx` attend
 *     leur premiere lecture avec celle des droits, si bien que le dock ne
 *     s'affiche jamais vide puis plein.
 *
 * AUCUN REPLI : document absent = aucune app, et la production.
 */

/** Le compte ne peut pas ecrire dans l'org master : sa liste suit le depot. */
const ORG_MASTER_ID = 'master';

export function clefDeCacheEnvironnement(orgId: string): string {
  return `taki.environnement.${orgId}`;
}

function lireCache(orgId: string): Environnement | undefined {
  try {
    const brut = window.localStorage.getItem(clefDeCacheEnvironnement(orgId));
    return brut === 'staging' || brut === 'production' ? brut : undefined;
  } catch {
    return undefined;
  }
}

function ecrireCache(orgId: string, environnement: Environnement): void {
  try {
    window.localStorage.setItem(clefDeCacheEnvironnement(orgId), environnement);
  } catch {
    /* Sans cache, la bande arrive apres la premiere lecture : rien a dire. */
  }
}

/** L'environnement de l'org, lu sans compte. `undefined` tant qu'aucune valeur n'est connue. */
export function useEnvironnementDeLHote(sdk: TakiSdk, orgId: string): Environnement | undefined {
  const [environnement, setEnvironnement] = useState<Environnement | undefined>(() => lireCache(orgId));
  useEffect(() => {
    let vivant = true;
    let arreter: (() => void) | undefined;
    try {
      arreter = sdk.environnement.observer(
        (valeur) => {
          if (!vivant) return;
          setEnvironnement(valeur);
          ecrireCache(orgId, valeur);
        },
        (cause: unknown) => console.warn("Environnement de l'org : lecture impossible.", cause),
      );
    } catch (cause) {
      console.warn("Environnement de l'org : observation impossible.", cause);
    }
    return () => {
      vivant = false;
      arreter?.();
    };
  }, [sdk, orgId]);
  return environnement;
}

export interface DroitsDInstallation {
  /** Owner ou admin actif (le droit de la gestion des membres). */
  peutGerer: boolean;
  /** Owner actif. */
  estOwner: boolean;
}

export function useInstallationDeLHote(
  sdk: TakiSdk,
  orgId: string,
  email: string | null,
  environnement: Environnement | undefined,
  droits: DroitsDInstallation,
): InstallationPort {
  const [apps, setApps] = useState<readonly string[]>([]);
  const [posee, setPosee] = useState(false);
  const [pret, setPret] = useState(false);

  useEffect(() => {
    if (email === null) {
      setApps([]);
      setPosee(false);
      setPret(false);
      return;
    }
    let vivant = true;
    setPret(false);
    let arreter: (() => void) | undefined;
    const echec = (cause: unknown) => {
      if (!vivant) return;
      // Illisible n'est pas « tout installe » : aucune app, et la console le dit.
      console.warn("Apps installees : lecture impossible, aucune app n'est montee.", cause);
      setApps([]);
      setPosee(false);
      setPret(true);
    };
    try {
      arreter = sdk.appsInstallees.observer((liste) => {
        if (!vivant) return;
        setApps(liste ?? []);
        setPosee(liste !== null);
        setPret(true);
      }, echec);
    } catch (cause) {
      echec(cause);
    }
    return () => {
      vivant = false;
      arreter?.();
    };
  }, [sdk, email]);

  /* LE FUSEAU REGLE, lu une fois connecte : seul l'onglet Environnement le
     montre. `sdk.timezone`, lui, le suit des la lecture d'avant connexion
     (`useEnvironnementDeLHote`). */
  const [fuseau, setFuseau] = useState<string | null>(null);
  useEffect(() => {
    if (email === null) return;
    let vivant = true;
    let arreter: (() => void) | undefined;
    try {
      arreter = sdk.environnement.observerFuseau(
        (valeur) => {
          if (vivant) setFuseau(valeur);
        },
        (cause: unknown) => console.warn("Fuseau de l'org : lecture impossible.", cause),
      );
    } catch (cause) {
      console.warn("Fuseau de l'org : observation impossible.", cause);
    }
    return () => {
      vivant = false;
      arreter?.();
    };
  }, [sdk, email]);

  const master = orgId === ORG_MASTER_ID;
  const raisonMaster = "Org master : la liste suit le dépôt, posée par pnpm dev:local.";
  return useMemo<InstallationPort>(
    () => ({
      apps,
      pret,
      posee,
      environnement: environnement ?? 'production',
      peutGererLesApps: droits.peutGerer && !master,
      peutChangerLEnvironnement: droits.estOwner && !master,
      raisonApps: master ? raisonMaster : droits.peutGerer ? null : 'Réservé aux administrateurs.',
      raisonEnvironnement: master ? raisonMaster : droits.estOwner ? null : 'Réservé au propriétaire.',
      installer: (appId) => sdk.appsInstallees.installer(appId),
      desinstaller: (appId) => sdk.appsInstallees.desinstaller(appId),
      changerEnvironnement: (valeur) => sdk.environnement.changer(valeur),
      fuseau,
      peutChangerLeFuseau: droits.estOwner,
      changerFuseau: (valeur) => sdk.environnement.changerFuseau(valeur),
    }),
    [apps, pret, posee, environnement, fuseau, droits.peutGerer, droits.estOwner, master, sdk],
  );
}
